import { Temporal } from "temporal-polyfill";

/**
 *
 * @description
 * Return the distance between the given dates in words. based on the same returned string from date-fns formatDistance function,
 * but adapted to use Temporal
 *
 * | Distance between dates                                            | Result              |
 * |-------------------------------------------------------------------|---------------------|
 * | 0 ... 30 secs                                                     | less than a minute  |
 * | 30 secs ... 1 min 30 secs                                         | 1 minute            |
 * | 1 min 30 secs ... 44 mins 30 secs                                 | [2..44] minutes     |
 * | 44 mins ... 30 secs ... 89 mins 30 secs                           | about 1 hour        |
 * | 89 mins 30 secs ... 23 hrs 59 mins 30 secs                        | about [2..24] hours |
 * | 23 hrs 59 mins 30 secs ... 41 hrs 59 mins 30 secs                 | 1 day               |
 * | 41 hrs 59 mins 30 secs ... 29 days 23 hrs 59 mins 30 secs         | [2..30] days        |
 * | 29 days 23 hrs 59 mins 30 secs ... 44 days 23 hrs 59 mins 30 secs | about 1 month       |
 * | 44 days 23 hrs 59 mins 30 secs ... 59 days 23 hrs 59 mins 30 secs | about 2 months      |
 * | 59 days 23 hrs 59 mins 30 secs ... 1 yr                           | [2..12] months      |
 * | 1 yr ... 1 yr 3 months                                            | about 1 year        |
 * | 1 yr 3 months ... 1 yr 9 month s                                  | over 1 year         |
 * | 1 yr 9 months ... 2 yrs                                           | almost 2 years      |
 * | N yrs ... N yrs 3 months                                          | about N years       |
 * | N yrs 3 months ... N yrs 9 months                                 | over N years        |
 * | N yrs 9 months ... N+1 yrs                                        | almost N+1 years    |
 *
 *
 * @param laterDate - The date
 * @param earlierDate - The date to compare with
 *
 * @returns The distance in words
 *
 * @example
 * // What is the distance between 2 July 2014 and 1 January 2015?
 * const result = formatDistance(new Date(2014, 6, 2), new Date(2015, 0, 1))
 * //=> '6 months ago'
 *
 * @example
 * // What is the distance between 1 January 2015 00:00:15
 * // and 1 January 2015 00:00:00, including seconds?
 * const result = formatDistance(
 *   new Date(2015, 0, 1, 0, 0, 15),
 *   new Date(2015, 0, 1, 0, 0, 0),
 * )
 * //=> 'less than 20 seconds ago'
 */
export function formatDistance(
  laterTime: Temporal.Instant | Date,
  earlierTime: Temporal.Instant | Date,
): string {
  const laterTimeTemporal =
    laterTime instanceof Date
      ? Temporal.Instant.from(laterTime.toISOString())
      : laterTime;
  const earlierTimeTemporal =
    earlierTime instanceof Date
      ? Temporal.Instant.from(earlierTime.toISOString())
      : earlierTime;

  // count the number of mins between the two temporal instants
  const minutes = laterTimeTemporal
    .since(earlierTimeTemporal, {
      largestUnit: "minute",
    })
    .abs().minutes;

  const isPast =
    Temporal.Instant.compare(laterTimeTemporal, earlierTimeTemporal) === -1;
  const suffix = !isPast ? "from now" : "ago";

  // count the number of months between the two temporal instants
  const earlierDate = Temporal.PlainDate.from({
    year: earlierTimeTemporal.toZonedDateTimeISO("UTC").year,
    month: earlierTimeTemporal.toZonedDateTimeISO("UTC").month,
    day: earlierTimeTemporal.toZonedDateTimeISO("UTC").day,
  });
  const laterDate = Temporal.PlainDate.from({
    year: laterTimeTemporal.toZonedDateTimeISO("UTC").year,
    month: laterTimeTemporal.toZonedDateTimeISO("UTC").month,
    day: laterTimeTemporal.toZonedDateTimeISO("UTC").day,
  });

  const days = laterDate.since(earlierDate, { largestUnit: "day" }).abs().days;
  const months = laterDate
    .since(earlierDate, { largestUnit: "month" })
    .abs().months;

  // 0 up to 2 mins
  if (minutes < 1) {
    return `less than 1 minute ${suffix}`;
  } else if (minutes < 2) {
    return `1 minute ${suffix}`;
  } else if (minutes < 45) {
    return `${Math.round(minutes)} minutes ${suffix}`;
    // 0.75 hrs up to 1.5 hrs
  } else if (minutes < 90) {
    return `about 1 hour ${suffix}`;

    // 1.5 hrs up to 24 hrs
  } else if (days < 1) {
    const hours = Math.round(minutes / 60);
    return `${hours} hours ${suffix}`;

    // 1 day up to 1.75 days
  } else if (days < 2) {
    return `1 day ${suffix}`;

    // less than 1 month
  } else if (days <= 31) {
    return `${days} days ${suffix}`;

    // 1 month up to 2 months
  } else if (months < 2) {
    return `about 1 month ${suffix}`;
  }

  // 2 months up to 12 months
  if (months < 12) {
    return `about ${Math.round(months)} months ${suffix}`;
    // 1 year up to max Date
  } else {
    const monthsSinceStartOfYear = months % 12;
    const years = Math.trunc(months / 12);

    // N years up to 1 years 3 months
    if (monthsSinceStartOfYear <= 3) {
      return `about ${years} year${years > 1 ? "s" : ""} ${suffix}`;

      // N years 3 months up to N years 9 months
    } else if (monthsSinceStartOfYear <= 9) {
      return `over ${years} year${years > 1 ? "s" : ""} ${suffix}`;

      // N years 9 months up to N year 12 months
    } else {
      return `almost ${years + 1} year${years + 1 > 1 ? "s" : ""} ${suffix}`;
    }
  }
}
